import type {
  Brand,
  BrandMetrics,
  BrandWithMetrics,
  AddBrandData,
  UpdateBrandData,
} from '../types/brand.types';
import type { BrandLogoStoragePort } from '../ports/brandLogoStorage.port';
import type { BrandContactsPort } from '../ports/brandContacts.port';

export class BrandService {
  constructor(
    private logoStorage?: BrandLogoStoragePort,
    private contactsPort?: BrandContactsPort
  ) {}

  /**
   * Processes logo upload with automated rollback if the subsequent database insert fails.
   */
  async handleAddBrandWithRollback(
    orgId: string,
    brandData: AddBrandData,
    dbInsertFn: (data: Omit<AddBrandData, 'logo_file' | 'primary_contact_id'> & { logo_url: string | null; organization_id: string }) => Promise<Brand>,
    storage?: BrandLogoStoragePort,
    contacts?: BrandContactsPort
  ): Promise<Brand> {
    const logoStorage = storage || this.logoStorage;
    const contactsPort = contacts || this.contactsPort;
    const { primary_contact_id, logo_file, ...brandDataWithoutContact } = brandData;
    let logoUrl = brandDataWithoutContact.logo_url || null;
    let uploadedPath: string | null = null;

    if (logo_file && logoStorage) {
      const uploadResult = await logoStorage.uploadLogo(orgId, brandData.name, logo_file);
      uploadedPath = uploadResult.path;
      logoUrl = uploadResult.publicUrl;
    }

    try {
      const createdBrand = await dbInsertFn({
        ...brandDataWithoutContact,
        logo_url: logoUrl,
        organization_id: orgId,
      });

      // Link primary contact if specified
      if (primary_contact_id && contactsPort && createdBrand.id) {
        try {
          await contactsPort.linkBrandToContact(primary_contact_id, createdBrand.id);
        } catch (contactErr) {
          console.error('Failed to link primary contact to brand:', contactErr);
        }
      }

      return createdBrand;
    } catch (dbError) {
      // Storage rollback: if database insert failed, delete uploaded logo to avoid orphan files
      if (uploadedPath && logoStorage) {
        try {
          await logoStorage.deleteLogo(uploadedPath);
        } catch (rollbackErr) {
          console.error('Failed to rollback uploaded logo file:', rollbackErr);
        }
      }
      throw dbError;
    }
  }

  /**
   * Processes update with optional logo upload.
   */
  async handleUpdateBrand(
    orgId: string,
    brandData: UpdateBrandData,
    dbUpdateFn: (data: Omit<UpdateBrandData, 'logo_file'> & { logo_url?: string | null }) => Promise<Brand>,
    storage?: BrandLogoStoragePort
  ): Promise<Brand> {
    const logoStorage = storage || this.logoStorage;
    const { logo_file, ...dataToUpdate } = brandData;
    let logoUrl = dataToUpdate.logo_url;

    if (logo_file && logoStorage && brandData.name) {
      const uploadResult = await logoStorage.uploadLogo(orgId, brandData.name, logo_file);
      logoUrl = uploadResult.publicUrl;
    }

    return dbUpdateFn({
      ...dataToUpdate,
      ...(logoUrl !== undefined ? { logo_url: logoUrl } : {}),
    });
  }

  /**
   * Overlays active and total campaign counts on brand metrics.
   */
  overlayCampaignMetrics(
    metrics: BrandMetrics,
    campaigns: Array<{ brand_id?: string | null; brand?: string | null; brand_name?: string | null; status: string }>,
    brandId: string,
    brandName?: string
  ): BrandMetrics {
    if (!campaigns || campaigns.length === 0) return metrics;

    const matchingCampaigns = campaigns.filter(
      (c) =>
        (brandId && c.brand_id === brandId) ||
        (brandName && c.brand && c.brand.toLowerCase() === brandName.toLowerCase()) ||
        (brandName && c.brand_name && c.brand_name.toLowerCase() === brandName.toLowerCase())
    );

    const activeCount = matchingCampaigns.filter((c) => c.status === 'active').length;

    return {
      ...metrics,
      totalCampaigns: matchingCampaigns.length,
      activeCampaigns: activeCount,
    };
  }

  /**
   * Overlays active and total campaign counts for multiple brands.
   */
  overlayAllBrandMetrics(
    brands: BrandWithMetrics[],
    campaigns: Array<{ brand_id?: string | null; brand?: string | null; brand_name?: string | null; status: string }>,
    showOnlyActive: boolean = false
  ): BrandWithMetrics[] {
    if (!campaigns) return brands;

    const brandList = brands.map((brand) => {
      const brandMatchingCampaigns = campaigns.filter(
        (c) =>
          c.brand_id === brand.id ||
          (c.brand && brand.name && c.brand.toLowerCase() === brand.name.toLowerCase()) ||
          (c.brand_name && brand.name && c.brand_name.toLowerCase() === brand.name.toLowerCase())
      );
      const activeCount = brandMatchingCampaigns.filter((c) => c.status === 'active').length;

      return {
        ...brand,
        totalCampaigns: brandMatchingCampaigns.length,
        activeCampaigns: activeCount,
      };
    });

    if (showOnlyActive) {
      return brandList.filter((b) => b.activeCampaigns > 0);
    }

    return brandList;
  }
}

export const brandService = new BrandService();

